import React, { useEffect, useRef, useState } from 'react';
import { Hash, RefreshCw, Save, AlertTriangle, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { STRATEGIES, CASING_OPTIONS, TEMPLATE_TOKENS } from '../config/codeStrategies';

const CodePolicySection = ({
  theme,
  styles,
  clientName,
  policy,
  draft,
  loading,
  saving,
  isDirty,
  previewCodes,
  previewLoading,
  previewError,
  warnings,
  onUpdateDraft,
  onReset,
  onPreview,
  onSave,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(draft.strategy === 'TEMPLATE');
  const debounceRef = useRef(null);

  // Debounced live preview: re-render sample codes ~400ms after the last
  // edit, rather than on every keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onPreview();
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.prefix, draft.separator, draft.suffix, draft.strategy, draft.bodyLength, draft.padWithZeros, draft.casing, draft.alphabet, draft.template, draft.startAt]);

  const cardStyle = {
    background: theme === 'light' ? 'rgba(139,92,246,0.03)' : 'rgba(139,92,246,0.06)',
    border: theme === 'light' ? '1.5px solid rgba(139,92,246,0.18)' : '1.5px solid rgba(139,92,246,0.25)',
    borderRadius: '14px',
    padding: '20px',
    marginBottom: '20px',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: theme === 'light' ? '#475569' : 'rgba(255,255,255,0.7)',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  };

  const fieldStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: theme === 'light' ? '1.5px solid #e2e8f0' : '1.5px solid rgba(255,255,255,0.1)',
    background: theme === 'light' ? '#ffffff' : 'rgba(15,23,42,0.5)',
    color: theme === 'light' ? '#0f172a' : '#ffffff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const isNewPolicy = !policy;

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.6 }}>
          <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: '13px' }}>Loading code policy…</span>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Hash size={16} color="#8b5cf6" />
          <div>
            <h4 style={{ fontSize: '14px', fontWeight: '700', color: theme === 'light' ? '#0f172a' : '#ffffff', margin: 0 }}>
              Infinity Code Format
            </h4>
            <p style={{ fontSize: '11.5px', color: theme === 'light' ? '#64748b' : 'rgba(255,255,255,0.5)', margin: '3px 0 0 0' }}>
              {isNewPolicy
                ? 'No format configured yet — new employees get a plain numeric code.'
                : 'Codes issued from here on follow this format. Already-issued codes never change.'}
            </p>
          </div>
        </div>
        {policy?.isLocked && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '10.5px', fontWeight: '700', color: '#dc2626',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: '6px', padding: '3px 8px', flexShrink: 0,
          }}>
            <Lock size={11} /> Locked
          </span>
        )}
      </div>

      {warnings?.length > 0 && (
        <div style={{
          display: 'flex', gap: '8px', alignItems: 'flex-start',
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: '8px', padding: '10px 12px', marginBottom: '14px',
        }}>
          <AlertTriangle size={14} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '12px', color: '#92400e', lineHeight: 1.5 }}>
            {warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        </div>
      )}

      {/* Prefix / separator / suffix */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: '12px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Prefix</label>
          <input
            type="text"
            value={draft.prefix}
            onChange={(e) => onUpdateDraft('prefix', e.target.value.toUpperCase())}
            placeholder="RAY"
            maxLength={12}
            disabled={policy?.isLocked}
            style={fieldStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Separator</label>
          <select
            value={draft.separator}
            onChange={(e) => onUpdateDraft('separator', e.target.value)}
            style={fieldStyle}
          >
            <option value="-">-</option>
            <option value="_">_</option>
            <option value="/">/</option>
            <option value="">(none)</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Suffix (optional)</label>
          <input
            type="text"
            value={draft.suffix || ''}
            onChange={(e) => onUpdateDraft('suffix', e.target.value)}
            placeholder="e.g. EG"
            style={fieldStyle}
          />
        </div>
      </div>

      {/* Strategy chips */}
      <label style={labelStyle}>Format</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px', marginBottom: '16px' }}>
        {STRATEGIES.map((s) => {
          const isActive = draft.strategy === s.value;
          return (
            <div
              key={s.value}
              onClick={() => {
                if (policy?.isLocked) return;
                onUpdateDraft('strategy', s.value);
                setShowAdvanced(s.value === 'TEMPLATE');
              }}
              style={{
                border: isActive ? '2px solid #8b5cf6' : theme === 'light' ? '1.5px solid #e2e8f0' : '1.5px solid rgba(255,255,255,0.1)',
                borderRadius: '10px',
                padding: '10px 12px',
                cursor: policy?.isLocked ? 'not-allowed' : 'pointer',
                opacity: policy?.isLocked && !isActive ? 0.5 : 1,
                background: isActive
                  ? theme === 'light' ? 'rgba(139,92,246,0.06)' : 'rgba(139,92,246,0.12)'
                  : theme === 'light' ? '#ffffff' : 'rgba(15,23,42,0.4)',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: '12px', fontWeight: '700', color: isActive ? '#8b5cf6' : theme === 'light' ? '#374151' : 'rgba(255,255,255,0.85)' }}>
                {s.label}
              </div>
              <div style={{ fontSize: '10.5px', color: theme === 'light' ? '#94a3b8' : 'rgba(255,255,255,0.4)', margin: '2px 0 6px 0' }}>
                {s.hint}
              </div>
              <code style={{
                fontSize: '11px', fontWeight: '600',
                color: theme === 'light' ? '#0f172a' : '#e2e8f0',
                background: theme === 'light' ? '#f8fafc' : 'rgba(0,0,0,0.25)',
                padding: '2px 6px', borderRadius: '4px',
              }}>
                {s.sample(draft.prefix, draft.separator)}
              </code>
            </div>
          );
        })}
      </div>

      {/* Sequential / random controls */}
      {draft.strategy !== 'TEMPLATE' ? (
        <div style={{ display: 'grid', gridTemplateColumns: draft.strategy === 'SEQUENTIAL' ? '1fr 1fr 1fr' : '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>{draft.strategy === 'SEQUENTIAL' ? 'Digits' : 'Length'}</label>
            <input
              type="number"
              min={1}
              max={20}
              value={draft.bodyLength}
              onChange={(e) => onUpdateDraft('bodyLength', Number(e.target.value))}
              style={fieldStyle}
            />
          </div>
          {draft.strategy === 'SEQUENTIAL' && (
            <>
              <div>
                <label style={labelStyle}>Zero-padded</label>
                <select
                  value={draft.padWithZeros ? 'yes' : 'no'}
                  onChange={(e) => onUpdateDraft('padWithZeros', e.target.value === 'yes')}
                  style={fieldStyle}
                >
                  <option value="yes">Yes (0001, 0002…)</option>
                  <option value="no">No (1, 2, 3…)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Start at</label>
                <input
                  type="number"
                  min={0}
                  value={draft.startAt}
                  onChange={(e) => onUpdateDraft('startAt', Number(e.target.value))}
                  style={fieldStyle}
                />
              </div>
            </>
          )}
          {draft.strategy !== 'SEQUENTIAL' && (
            <div>
              <label style={labelStyle}>Casing</label>
              <select value={draft.casing} onChange={(e) => onUpdateDraft('casing', e.target.value)} style={fieldStyle}>
                {CASING_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Template</label>
          <input
            type="text"
            value={draft.template || ''}
            onChange={(e) => onUpdateDraft('template', e.target.value)}
            placeholder="{PREFIX}-{YY}-{SEQ:4}"
            style={{ ...fieldStyle, fontFamily: 'monospace' }}
          />
        </div>
      )}

      {/* Advanced toggle (casing when sequential, alphabet override, template legend) */}
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'none', border: 'none', cursor: 'pointer',
          color: theme === 'light' ? '#64748b' : 'rgba(255,255,255,0.6)',
          fontSize: '12px', fontWeight: '600', padding: 0, marginBottom: showAdvanced ? '12px' : '16px',
        }}
      >
        {showAdvanced ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        Advanced options
      </button>

      {showAdvanced && (
        <div style={{
          background: theme === 'light' ? '#f8fafc' : 'rgba(0,0,0,0.15)',
          borderRadius: '8px', padding: '12px', marginBottom: '16px',
        }}>
          {draft.strategy === 'SEQUENTIAL' && (
            <div style={{ marginBottom: '10px' }}>
              <label style={labelStyle}>Casing</label>
              <select value={draft.casing} onChange={(e) => onUpdateDraft('casing', e.target.value)} style={fieldStyle}>
                {CASING_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          {draft.strategy.startsWith('RANDOM_') && (
            <div style={{ marginBottom: '10px' }}>
              <label style={labelStyle}>Custom alphabet (optional)</label>
              <input
                type="text"
                value={draft.alphabet || ''}
                onChange={(e) => onUpdateDraft('alphabet', e.target.value)}
                placeholder="Leave blank for the default ambiguity-safe set"
                style={fieldStyle}
              />
            </div>
          )}
          {draft.strategy === 'TEMPLATE' && (
            <div>
              <div style={{ fontSize: '11.5px', fontWeight: '600', marginBottom: '6px', color: theme === 'light' ? '#374151' : 'rgba(255,255,255,0.8)' }}>
                Available tokens
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                {TEMPLATE_TOKENS.map((t) => (
                  <div key={t.token} style={{ fontSize: '11px', color: theme === 'light' ? '#64748b' : 'rgba(255,255,255,0.5)' }}>
                    <code style={{ fontWeight: '700', color: theme === 'light' ? '#0f172a' : '#e2e8f0' }}>{t.token}</code> — {t.desc}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Live preview */}
      <div style={{
        background: theme === 'light' ? '#ffffff' : 'rgba(0,0,0,0.2)',
        border: theme === 'light' ? '1px dashed rgba(139,92,246,0.35)' : '1px dashed rgba(139,92,246,0.4)',
        borderRadius: '8px', padding: '12px', marginBottom: '16px',
      }}>
        <div style={{ fontSize: '10.5px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8b5cf6', marginBottom: '8px' }}>
          Next codes will look like
        </div>
        {previewLoading ? (
          <span style={{ fontSize: '12px', opacity: 0.6 }}>Generating preview…</span>
        ) : previewError ? (
          <span style={{ fontSize: '12px', color: '#dc2626' }}>{previewError}</span>
        ) : previewCodes.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {previewCodes.map((code, i) => (
              <code key={i} style={{
                fontSize: '12px', fontWeight: '600', padding: '4px 10px', borderRadius: '6px',
                background: theme === 'light' ? '#f1f5f9' : 'rgba(255,255,255,0.08)',
                color: theme === 'light' ? '#0f172a' : '#ffffff',
              }}>
                {code}
              </code>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: '12px', opacity: 0.5 }}>Enter a prefix to see a preview</span>
        )}
      </div>

      {/* Footer stats + actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '11px', color: theme === 'light' ? '#94a3b8' : 'rgba(255,255,255,0.4)' }}>
          {policy
            ? <>Issued: <strong>{policy.issuedCount ?? 0}</strong> · Last: <strong>{policy.lastIssuedCode || 'n/a'}</strong> · Next seq: <strong>{policy.nextSequence}</strong></>
            : 'This client has never had a code policy.'}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isDirty && (
            <button
              type="button"
              onClick={onReset}
              style={{
                background: 'transparent',
                color: theme === 'light' ? '#64748b' : 'rgba(255,255,255,0.6)',
                border: theme === 'light' ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px', padding: '8px 14px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Discard changes
            </button>
          )}
          <button
            type="button"
            onClick={() => onSave(clientName)}
            disabled={saving || !draft.prefix?.trim() || !isDirty}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
              color: '#ffffff', border: 'none', borderRadius: '8px',
              padding: '8px 16px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer',
              opacity: saving || !draft.prefix?.trim() || !isDirty ? 0.5 : 1,
            }}
          >
            {saving ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={13} />}
            {saving ? 'Saving…' : 'Save Format'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodePolicySection;
